import { PropsWithChildren } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as Avatar from '@radix-ui/react-avatar';

import { ChevronRightIcon, ImagePlaceholderIcon } from '@anyx/shared-icons';


interface LineMessageBlockContainerProps {
  title: string;
  tableHeader?: [string, string];
}

interface LineMessageBlockImageProps {
  imageUrl: string;
  label?: string | null;
}

export const LineMessageBlockContainer = ({
  children,
  title,
  tableHeader,
}: PropsWithChildren<LineMessageBlockContainerProps>) => {
  return (
    <div>
      <h4 className="mb-2 font-semibold">{title}</h4>
      <ul className="crm-line-message-preview">
        {tableHeader && tableHeader?.length > 1 && (
          <li className="crm-line-message-preview__list-item bg-gray-1 text-2xs h-8">
            <p>{tableHeader[0]}</p>
            <p>{tableHeader[1]}</p>
          </li>
        )}
        {children}
      </ul>
    </div>
  );
};

export const LineMessageBlockImage = ({ imageUrl, label }: LineMessageBlockImageProps) => {
  return (
    <LineMessageBlockPreview>
      <div className="flex items-center gap-x-2">
        <Avatar.Root className="h-8 w-8">
          <Avatar.Image
            src={imageUrl}
            alt={label ?? ''}
            className="h-8 w-8 object-cover object-center"
          />
          <Avatar.Fallback delayMs={1000}>
            <div className="bg-gray-1 flex h-full w-full items-center justify-center">
              <ImagePlaceholderIcon className="text-gray-5 h-6 w-6" />
            </div>
          </Avatar.Fallback>
        </Avatar.Root>
        <p className="line-clamp-1 w-fit">{label ? label : imageUrl}</p>
      </div>
    </LineMessageBlockPreview>
  );
};

export const LineMessageBlockPreview = ({ children }: PropsWithChildren) => {
  return (
    <div className="flex items-center justify-between gap-x-3">
      <div className="line-clamp-1">{children}</div>
      <ChevronRightIcon className="shrink-0 stroke-1" />
    </div>
  );
};

export const LineMessageBlockUrlPreview = ({ url }: { url?: string }) => {
  const { t, i18n } = useTranslation('crm');

  if (!url) return <p className="text-gray-5">{t('crm.lineMessagePreview.noAction')}</p>;

  return (
    <Trans
      i18n={i18n}
      i18nKey="crm.lineMessagePreview.url"
      ns="crm"
      components={[<span className="text-gray-7" />]}
      parent="p"
      className="truncate"
      values={{ url }}
    />
  );
};
